const getSiteUrl = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return 'https://swift-tools-kit.com'; // Fallback for SSR
};

const createBreadcrumbList = (items) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${getSiteUrl()}${item.url}`,
    })),
  };
};

const createSoftwareApplication = (toolData, toolKey) => {
  const siteUrl = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: toolData.title,
    description: toolData.description,
    applicationCategory: 'DeveloperApplication',
    operatingSystem: 'iOS',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    url: `${siteUrl}${toolData.canonical}`,
    featureList: toolData.features || [],
    softwareVersion: '1.0',
    author: {
      '@type': 'Organization',
      name: 'SwiftToolsKit',
      url: siteUrl,
    },
  };
};

const createWebApplication = (toolData, toolKey) => {
  const siteUrl = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: toolData.title,
    description: toolData.description,
    url: `${siteUrl}${toolData.canonical}`,
    browserRequirements: 'Requires JavaScript. Requires HTML5.',
    softwareVersion: '1.0',
    applicationCategory: 'DeveloperApplication',
    operatingSystem: 'Any',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
  };
};

export const SEO_DATA = {
  home: {
    title: 'SwiftToolsKit - Swift & SwiftUI Code Generator Tools for iOS Developers',
    description: 'Generate SwiftUI code instantly with our free online tools. Create loaders, gradients, cards, animations, alerts, and more. Convert JSON to Codable, generate API clients, and import Postman collections.',
    keywords: 'SwiftUI, Swift, iOS development, code generator, SwiftUI tools, UIKit converter, JSON to Codable, API generator',
    canonical: '/',
    image: '/swifttoolskitlogo.png',
    features: [
      'SwiftUI Code Generation',
      'Loader Components',
      'Gradient Generator',
      'Card Components',
      'Animation Generator',
      'Alert Generator',
      'Photo Picker',
      'JSON to Codable',
      'API Client Generator',
      'Storyboard Converter',
    ],
    get jsonLd() {
      const siteUrl = getSiteUrl();
      return [
        {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'SwiftToolsKit',
          url: siteUrl,
          description: this.description,
          publisher: {
            '@type': 'Organization',
            name: 'SwiftToolsKit',
            url: siteUrl,
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'SwiftToolsKit',
          url: siteUrl,
          logo: `${siteUrl}/swifttoolskitlogo.png`,
          description: 'Free Swift and SwiftUI code generator tools for iOS developers',
        },
      ];
    },
  },
  loader: {
    title: 'SwiftUI Loader Generator - iOS Loading Indicators | SwiftToolsKit',
    description: 'Generate SwiftUI loader code with ring, pulse, and dots animations. Free iOS loading indicator generator with customizable colors, sizes, and .isLoading() modifier support.',
    keywords: 'SwiftUI loader, iOS loading indicator, SwiftUI spinner, loading animation SwiftUI, SwiftUI activity indicator, iOS loader generator, SwiftUI progress indicator, SwiftUI loading view, SwiftUI spinner generator, free SwiftUI loader',
    canonical: '/tools/loader',
    image: '/swifttoolskitlogo.png',
    features: [
      'Ring Loader Animation',
      'Pulse Loader Animation',
      'Dots Loader Animation',
      'Customizable Colors',
      'Size Options',
      'isLoading() Modifier Support',
    ],
    get jsonLd() {
      return [
        createSoftwareApplication(this, 'loader'),
        createWebApplication(this, 'loader'),
        createBreadcrumbList([
          { name: 'Home', url: '/' },
          { name: 'Loader Generator', url: '/tools/loader' },
        ]),
      ];
    },
  },
  gradient: {
    title: 'SwiftUI Gradient Generator - Linear & Radial Gradients | SwiftToolsKit',
    description: 'Create SwiftUI gradient code instantly. Generate LinearGradient, RadialGradient, and AngularGradient with unlimited colors. Free iOS gradient generator tool.',
    keywords: 'SwiftUI gradient, iOS gradient generator, LinearGradient SwiftUI, RadialGradient SwiftUI, AngularGradient SwiftUI, gradient code generator, SwiftUI colors, iOS gradient tool, SwiftUI gradient background, free gradient generator',
    canonical: '/tools/gradient',
    image: '/swifttoolskitlogo.png',
    features: [
      'Linear Gradient',
      'Radial Gradient',
      'Angular Gradient',
      'Multiple Color Stops',
      'Custom Angle',
      'Export Code',
    ],
    get jsonLd() {
      return [
        createSoftwareApplication(this, 'gradient'),
        createWebApplication(this, 'gradient'),
        createBreadcrumbList([
          { name: 'Home', url: '/' },
          { name: 'Gradient Generator', url: '/tools/gradient' },
        ]),
      ];
    },
  },
  card: {
    title: 'SwiftUI Card Generator - Reusable Card Components | SwiftToolsKit',
    description: 'Generate SwiftUI card components for lists and grids. Customize shadows, backgrounds, dimensions, and layouts. Free iOS card UI generator with auto-layout support.',
    keywords: 'SwiftUI card, SwiftUI card component, iOS card UI, SwiftUI card generator, reusable card SwiftUI, SwiftUI list card, SwiftUI grid card, iOS card component, SwiftUI card view, card layout SwiftUI',
    canonical: '/tools/card',
    image: '/swifttoolskitlogo.png',
    features: [
      'Custom Dimensions',
      'Shadow Effects',
      'Background Colors',
      'Border Radius',
      'Static Layout',
      'Auto Layout',
      'View Tree Builder',
    ],
    get jsonLd() {
      return [
        createSoftwareApplication(this, 'card'),
        createWebApplication(this, 'card'),
        createBreadcrumbList([
          { name: 'Home', url: '/' },
          { name: 'Card Generator', url: '/tools/card' },
        ]),
      ];
    },
  },
  animation: {
    title: 'SwiftUI Animation Generator - Fade, Slide, Bounce | SwiftToolsKit',
    description: 'Create SwiftUI animations instantly. Generate fade, slide, bounce, shimmer, and staggered animations. Free iOS animation code generator with custom duration and curves.',
    keywords: 'SwiftUI animation, iOS animation code, SwiftUI transitions, animation generator Swift, SwiftUI fade animation, SwiftUI slide animation, SwiftUI bounce animation, SwiftUI shimmer effect, SwiftUI animation code, free SwiftUI animations',
    canonical: '/tools/animation',
    image: '/swifttoolskitlogo.png',
    features: [
      'Fade Animation',
      'Slide Animation',
      'Bounce Animation',
      'Shimmer Effect',
      'Staggered List Animation',
      'Custom Duration',
      'Easing Curves',
    ],
    get jsonLd() {
      return [
        createSoftwareApplication(this, 'animation'),
        createWebApplication(this, 'animation'),
        createBreadcrumbList([
          { name: 'Home', url: '/' },
          { name: 'Animation Generator', url: '/tools/animation' },
        ]),
      ];
    },
  },
  alert: {
    title: 'SwiftUI Custom Alert Generator - iOS Alert Dialogs | SwiftToolsKit',
    description: 'Generate custom SwiftUI alert components with success, error, warning, and info types. Free iOS alert dialog generator with blur effects and animations.',
    keywords: 'SwiftUI custom alert, iOS alert dialog, SwiftUI alert generator, custom alert SwiftUI, SwiftUI dialog, SwiftUI popup, iOS alert component, SwiftUI alert view, SwiftUI modal alert, free SwiftUI alerts',
    canonical: '/tools/alert',
    image: '/swifttoolskitlogo.png',
    features: [
      'Success Alert',
      'Error Alert',
      'Warning Alert',
      'Info Alert',
      'Custom Buttons',
      'Blur Effects',
      'Animations',
    ],
    get jsonLd() {
      return [
        createSoftwareApplication(this, 'alert'),
        createWebApplication(this, 'alert'),
        createBreadcrumbList([
          { name: 'Home', url: '/' },
          { name: 'Alert Generator', url: '/tools/alert' },
        ]),
      ];
    },
  },
  photoPicker: {
    title: 'SwiftUI Photo Picker Generator - Camera & Library | SwiftToolsKit',
    description: 'Generate SwiftUI photo picker code with camera and library support. Includes PhotosPicker API, multiple selection, cropping, and permission handling. Free iOS image picker.',
    keywords: 'SwiftUI photo picker, PhotosPicker SwiftUI, iOS image picker, camera SwiftUI, SwiftUI image picker, photo library SwiftUI, PhotosPicker API, iOS camera integration, SwiftUI photo selector, free photo picker SwiftUI',
    canonical: '/tools/photo-picker',
    image: '/swifttoolskitlogo.png',
    features: [
      'Camera Integration',
      'Photo Library Access',
      'Multiple Selection',
      'Image Cropping',
      'PhotosPicker API',
      'Permission Handling',
    ],
    get jsonLd() {
      return [
        createSoftwareApplication(this, 'photoPicker'),
        createWebApplication(this, 'photoPicker'),
        createBreadcrumbList([
          { name: 'Home', url: '/' },
          { name: 'Photo Picker Generator', url: '/tools/photo-picker' },
        ]),
      ];
    },
  },
  jsonToModel: {
    title: 'JSON to Swift Codable Generator - Convert JSON | SwiftToolsKit',
    description: 'Convert JSON to Swift Codable structs instantly. Free JSON parser with optional types, Swift 5.9+ macros, nested objects, and CodingKeys generation.',
    keywords: 'JSON to Swift Codable, JSON to Codable generator, Swift Codable generator, JSON parser Swift, Swift model generator, Codable converter, JSON to Swift struct, Swift Codable code generator, JSON decoder Swift, free Codable generator',
    canonical: '/tools/json-to-model',
    image: '/swifttoolskitlogo.png',
    features: [
      'JSON Parsing',
      'Codable Struct Generation',
      'Optional Types',
      'Nested Objects',
      'CodingKeys Support',
      'Swift 5.9+ Macros',
    ],
    get jsonLd() {
      return [
        createSoftwareApplication(this, 'jsonToModel'),
        createWebApplication(this, 'jsonToModel'),
        createBreadcrumbList([
          { name: 'Home', url: '/' },
          { name: 'JSON to Model Generator', url: '/tools/json-to-model' },
        ]),
      ];
    },
  },
  apiGenerator: {
    title: 'Swift API Client Generator - URLSession & Async/Await | SwiftToolsKit',
    description: 'Generate Swift API client code with async/await support. Create APIService classes, error handling, and endpoint management. Free URLSession code generator.',
    keywords: 'Swift API client, Swift API generator, URLSession generator, async await Swift, API code generator Swift, Swift network layer, Swift API service, URLSession Swift, Swift HTTP client, free API generator Swift',
    canonical: '/tools/api-generator',
    image: '/swifttoolskitlogo.png',
    features: [
      'Async/Await Support',
      'APIService Generation',
      'Error Handling',
      'Endpoint Management',
      'Request/Response Types',
      'URLSession Integration',
    ],
    get jsonLd() {
      return [
        createSoftwareApplication(this, 'apiGenerator'),
        createWebApplication(this, 'apiGenerator'),
        createBreadcrumbList([
          { name: 'Home', url: '/' },
          { name: 'API Generator', url: '/tools/api-generator' },
        ]),
      ];
    },
  },
  storyboardConverter: {
    title: 'UIKit to SwiftUI Converter - Storyboard Migration | SwiftToolsKit',
    description: 'Convert UIKit storyboard XML to SwiftUI code instantly. Transform UIButtons, UILabels, UIImageViews to SwiftUI. Free storyboard to SwiftUI migration tool.',
    keywords: 'UIKit to SwiftUI converter, storyboard to SwiftUI, UIKit migration tool, storyboard converter, migrate storyboard, UIKit to SwiftUI migration, storyboard SwiftUI converter, convert storyboard, free UIKit converter, SwiftUI migration tool',
    canonical: '/tools/storyboard-converter',
    image: '/swifttoolskitlogo.png',
    features: [
      'Storyboard XML Parsing',
      'UIKit to SwiftUI Conversion',
      'Auto Layout Conversion',
      'Component Detection',
      'Constraint Analysis',
      'Visual Preview',
      'Code Generation',
    ],
    get jsonLd() {
      return [
        createSoftwareApplication(this, 'storyboardConverter'),
        createWebApplication(this, 'storyboardConverter'),
        createBreadcrumbList([
          { name: 'Home', url: '/' },
          { name: 'Storyboard Converter', url: '/tools/storyboard-converter' },
        ]),
      ];
    },
  },
  postmanImporter: {
    title: 'Postman to Swift API Generator - Import Collections | SwiftToolsKit',
    description: 'Import Postman collections and generate Swift API client code automatically. Extract endpoints, headers, and generate APIService classes. Free Postman Swift converter.',
    keywords: 'Postman to Swift, Postman collection Swift, Postman Swift generator, API client from Postman, Postman importer Swift, Postman to SwiftUI, convert Postman to Swift, Postman API generator, free Postman converter, Postman Swift code generator',
    canonical: '/tools/postman-importer',
    image: '/swifttoolskitlogo.png',
    features: [
      'Postman Collection Import',
      'Endpoint Extraction',
      'Header Parsing',
      'Request Body Handling',
      'APIService Generation',
      'Async/Await Support',
    ],
    get jsonLd() {
      return [
        createSoftwareApplication(this, 'postmanImporter'),
        createWebApplication(this, 'postmanImporter'),
        createBreadcrumbList([
          { name: 'Home', url: '/' },
          { name: 'Postman Importer', url: '/tools/postman-importer' },
        ]),
      ];
    },
  },
};

export const getSEOData = (tool) => {
  return SEO_DATA[tool] || SEO_DATA.home;
};

